<template>
  <section
    v-if="templatesEnabled && leftTab === 'templates'"
    v-bind="$attrs"
    class="ren-decoration-template-page ren-panel">
    <RenDecorationTemplateDetail
      v-if="selectedMarketTemplate"
      :template="selectedMarketTemplate"
      :target-page-options="targetPageOptions"
      v-model:target-page-code="targetPageCode"
      :readonly="readonly"
      :applying-template="applyingTemplate"
      :template-actions-enabled="templateActionsEnabled"
      @back="closeTemplateDetail"
      @apply="applyTemplateFromMarket" />
    <RenDecorationTemplateMarket
      v-else
      :templates="templates"
      :readonly="readonly"
      :template-actions-enabled="templateActionsEnabled"
      @back="leftTab = 'components'"
      @refresh="$emit('refresh-templates')"
      @create-template="$emit('save-template', cloneSchema(draft))"
      @open-detail="openTemplateDetail"
      @edit-template="editTemplate"
      @copy-template="copyTemplate"
      @toggle-status="toggleTemplateEnabled"
      @restore-template="restoreTemplate"
      @delete-template="deleteTemplate"
      @delete-permanently="deleteTemplatePermanently" />
  </section>

  <section v-else v-bind="$attrs" class="ren-decoration-editor">
    <div v-if="templateBinding" class="ren-decoration-template-lock" role="status">
      <LockKeyhole :size="16" aria-hidden="true" />
      <span
        ><strong>模板锁定</strong> {{ templateBinding.templateCode }} · 草稿 {{ templateBinding.draftTemplateVersion
        }}<template v-if="templateBinding.publishedTemplateVersion">
          · 发布 {{ templateBinding.publishedTemplateVersion }}</template
        ></span
      >
    </div>
    <RenDecorationComponentLibrary
      :active-tab="leftTab"
      :definitions="palette.length ? [] : componentDefinitions"
      :eyebrow="eyebrow"
      :items="paletteItems"
      :readonly="readonly || !templateAccess.canAdd()"
      :shell-action-visible="Boolean(previewActiveTab?.pageCode)"
      :shell-description="
        previewContentSchema ? `来自独立页面 ${previewActiveTab?.pageCode}` : '尚未绑定可预览的独立页面'
      "
      :shell-page="tabbarShellPage"
      :shell-title="`${previewActiveTab?.text || '当前导航'}内容`"
      :subtitle="subtitle"
      :templates-enabled="templatesEnabled"
      :title="title"
      @add="addComponent"
      @drag="startDrag"
      @open-shell-page="previewActiveTab && openTabbarPageEditor(previewActiveTab)"
      @update:active-tab="leftTab = $event">
      <template #icon="{ item }">
        <component :is="paletteIconComponent(item)" :size="18" :stroke-width="1.8" />
      </template>
    </RenDecorationComponentLibrary>

    <RenDecorationCanvas
      :device="previewDevice.platform === 'tablet' ? 'tablet' : 'phone'"
      mode="live"
      :components="previewComponents"
      :readonly="readonly || shellCompositePreview"
      :selected-id="selectedComponent?.id">
      <div class="ren-decoration-canvas__floating-tools">
        <label class="ren-decoration-device-picker" title="切换预览设备">
          <Smartphone :size="13" :stroke-width="1.8" aria-hidden="true" />
          <RrSelect
            v-model:value="previewDeviceCode"
            size="small"
            aria-label="预览设备"
            :options="previewDeviceOptions"
          />
        </label>
      </div>
      <div class="ren-decoration-preview-viewport" ref="previewViewportRef">
      <div class="ren-decoration-preview-fit" :style="previewDeviceStyle">
      <div
        class="ren-decoration-phone"
        :class="`is-${previewDevice.platform}`"
        :data-device="previewDevice.code">
        <!-- 手机顶部状态栏 -->
        <div class="ren-decoration-phone__statusbar">
          <span class="statusbar-time">09:41</span>
          <div class="statusbar-icons">
            <span class="statusbar-signal" aria-hidden="true">
              <i></i><i></i><i></i><i></i>
            </span>
            <span class="statusbar-wifi" aria-hidden="true"></span>
            <span class="statusbar-battery" aria-hidden="true">
              <i class="battery-level"></i>
            </span>
          </div>
        </div>

        <!-- 微信小程序标准顶部导航栏与右上角胶囊控件 -->
        <div
          v-if="previewDevice.platform === 'mini-program'"
          class="ren-decoration-phone__miniapp-nav"
        >
          <div class="miniapp-nav-left"></div>
          <div class="miniapp-nav-title">{{ previewPageTitle || '微信小程序' }}</div>
          <div class="ren-decoration-phone__capsule" aria-label="微信小程序胶囊区">
            <button type="button" class="capsule-btn" title="更多" tabindex="-1">
              <span class="capsule-dots">
                <i></i><i></i><i></i>
              </span>
            </button>
            <span class="capsule-sep"></span>
            <button type="button" class="capsule-btn" title="关闭" tabindex="-1">
              <span class="capsule-circle">
                <i></i>
              </span>
            </button>
          </div>
        </div>

        <!-- App / H5 标准导航栏 -->
        <div
          v-else-if="!previewHasTabbar || previewHeaderMode === 'HIDDEN'"
          class="ren-decoration-phone__app-nav"
        >
          <span class="app-nav-title">{{ previewPageTitle || '页面标题' }}</span>
        </div>

        <div class="ren-decoration-phone__screen" :style="previewStageStyle">
          <div class="ren-decoration-phone__design">
            <div class="ren-decoration-phone__design-stage">
          <div
            class="ren-decoration-phone__body"
            :class="{ 'has-tabbar': previewHasTabbar }"
            :style="previewStageStyle"
            @dragover.prevent
            @drop="dropComponent">
            <header
              v-if="previewHasTabbar && previewHeaderMode !== 'HIDDEN'"
              class="ren-decoration-app-header"
              :class="{ 'is-compact': previewHeaderMode === 'COMPACT' }">
              <strong>{{ previewPageTitle }}</strong>
              <span v-if="previewHeaderMode !== 'COMPACT'">{{ previewActiveTab?.text || '首页' }}</span>
            </header>
            <article
              v-for="(component, index) in previewComponents"
              :key="component.id"
              :aria-disabled="readonly || shellCompositePreview"
              :aria-label="shellCompositePreview ? undefined : `选择组件 ${component.title || component.type}`"
              class="ren-decoration-preview-block"
              :class="{
                'is-active': !shellCompositePreview && component.id === selectedComponent?.id,
                'is-readonly': shellCompositePreview,
              }"
              :role="shellCompositePreview ? undefined : 'button'"
              :tabindex="shellCompositePreview ? -1 : 0"
              @click="selectPreviewComponent(component.id)"
              @keydown.enter.prevent="selectPreviewComponent(component.id)"
              @keydown.space.prevent="selectPreviewComponent(component.id)">
              <WidgetBox :style-source="component.style">
                <component
                  :is="previewComponentName(component.type)"
                  :component="component"
                  :component-data="previewDataFor(component)"
                  :preview-data="previewData"
                  :theme-color="previewThemeColor"
                  :theme-tokens="previewThemeTokens" />
              </WidgetBox>
              <span
                v-if="!shellCompositePreview"
                class="ren-decoration-preview-block__badge"
                :data-index="index + 1"
                aria-hidden="true"></span>
              <div v-if="!shellCompositePreview" class="ren-decoration-preview-block__actions" aria-label="组件操作">
                <button
                  :disabled="readonly || component.locked || !canMovePreviewComponent(component.id, -1)"
                  :title="previewActionTitle(component.id, 'move', '上移')"
                  type="button"
                  @click.stop="movePreviewComponent(component.id, -1)">
                  <LockKeyhole v-if="previewActionLocked(component.id, 'move')" :size="12" aria-hidden="true" />
                  <span>上移</span>
                </button>
                <button
                  :disabled="readonly || component.locked || !canMovePreviewComponent(component.id, 1)"
                  :title="previewActionTitle(component.id, 'move', '下移')"
                  type="button"
                  @click.stop="movePreviewComponent(component.id, 1)">
                  <LockKeyhole v-if="previewActionLocked(component.id, 'move')" :size="12" aria-hidden="true" />
                  <span>下移</span>
                </button>
                <button
                  class="is-danger"
                  :disabled="readonly || component.required || !templateAccess.canRemove(component.id)"
                  :title="previewActionTitle(component.id, 'remove', '删除')"
                  type="button"
                  @click.stop="removeComponentById(component.id)">
                  <LockKeyhole v-if="previewActionLocked(component.id, 'remove')" :size="12" aria-hidden="true" />
                  <span>删除</span>
                </button>
              </div>
            </article>
            <div v-if="previewComponents.length === 0" class="ren-decoration-preview-empty">
              <strong>{{ tabbarShellPage ? '当前导航暂无页面内容' : '当前页面暂无组件' }}</strong>
              <span>{{ previewEmptyMessage }}</span>
              <button
                v-if="tabbarShellPage && previewActiveTab?.pageCode"
                class="ren-button is-primary"
                type="button"
                @click="openTabbarPageEditor(previewActiveTab)">
                装修{{ previewActiveTab.text }}
              </button>
            </div>
            </div>
            </div>
          </div>
          <nav v-if="previewHasTabbar" class="ren-decoration-tabbar" aria-label="动态页面导航">
            <button
              v-for="item in previewTabbarItems"
              :key="item.code"
              :class="{
                active: item.code === previewActiveTabCode,
                prominent: item.prominent || previewTabbar?.prominentCode === item.code,
              }"
              type="button"
              @click="selectPreviewTab(item)">
              <i>{{ previewTabIcon(item) }}</i>
              <span>{{ item.text }}</span>
            </button>
          </nav>
        </div>
      </div>
      </div>
      </div>
    </RenDecorationCanvas>

    <RenDecorationInspector
      :active-tab="workspaceInspectorTab"
      :context="inspectorTab === 'page' ? 'page' : 'component'"
      :show-data-source-tab="showDataSourceTab"
      :title="selectedComponent?.title ?? '页面设置'"
      @update:active-tab="setWorkspaceInspectorTab"
      @update:context="setInspectorContext">
      <div v-if="inspectorTab === 'page'" class="ren-decoration-inspector__body">
        <PageInspector
          :page="draft.page"
          :tabbar="editableTabbar"
          :theme-tokens="themeTokens"
          :layout-mode="layoutMode"
          :header-mode="draftHeaderMode"
          :tabbar-shell-page="tabbarShellPage"
          :preview-has-tabbar="previewHasTabbar"
          :page-code="pageCode"
          :schema-json="schemaJson"
          :readonly="readonly"
          :template-bound="Boolean(templateBinding)"
          :can-apply-theme-preset="canApplyThemePreset"
          :link-target-count="linkTargets.length"
          :can-edit-page="(key) => templateAccess.canEditPage(key)"
          @update:page="setPageValue"
          @apply-preset="applyThemePreset"
          @tabbar-change="(key, value) => setTabbarValue(key as keyof RenDecorationTabBar, value as never)"
          @tabbar-item-change="(index, key, value) => setTabbarItemValue(index, key as keyof RenDecorationTabBarItem, value as never)"
          @tabbar-item-code="setTabbarItemCode"
          @tabbar-add="addTabbarItem"
          @tabbar-move="moveTabbarItem"
          @tabbar-remove="removeTabbarItem"
          @tabbar-action="setTabbarItemAction"
          @tabbar-prominent="setTabbarProminent"
          @tabbar-open-nav="openTabbarNavigationDialog"
          @tabbar-open-page="openTabbarPageEditor"
          @select-app="emit('select-page', 'app')"
        />
      </div>

      <div v-else-if="selectedComponent" class="ren-decoration-inspector__body">
        <template v-if="inspectorTab === 'component'">
          <div class="ren-decoration-toolbar">
            <RrButton
              size="small"
              :disabled="readonly || selectedComponent.locked || !templateAccess.canAdd()"
              :title="lockTitle(templateAccess.canAdd(), '复制')"
              @click="duplicateComponent">
              <TemplateLockMark :locked="templateLocked(templateAccess.canAdd())" />复制
            </RrButton>
          </div>
          <div class="ren-decoration-field-group">
            <strong>
              {{ selectedComponent.title }}
              <TemplateLockMark :locked="templateLocked(templateAccess.canEditProp(selectedComponent.id, 'title'))" />
            </strong>
          </div>
          <p v-if="selectedComponent.required || selectedComponent.locked" class="ren-decoration-layout-hint">
            {{ selectedComponent.required ? '这是固化页面的必需区块，不能删除。' : '' }}
            {{ selectedComponent.locked ? '该区块位置已锁定。' : '' }}
          </p>
          <div class="ren-decoration-switch-cell">
            <span
              >启用组件
              <TemplateLockMark :locked="templateLocked(templateAccess.canEditProp(selectedComponent.id, 'enabled'))"
            /></span>
            <RrSwitch
              :checked="selectedComponent.enabled"
              :disabled="readonly || !templateAccess.canEditProp(selectedComponent.id, 'enabled')"
              :title="lockTitle(templateAccess.canEditProp(selectedComponent.id, 'enabled'))"
              @update:checked="(val) => setSelectedEnabled(Boolean(val))" />
          </div>
          <section class="ren-decoration-navigation-card">
            <header>
              <div>
                <strong
                  >{{ componentNavigationTitle }}
                  <TemplateLockMark
                    :locked="templateLocked(templateAccess.canEditProp(selectedComponent.id, 'navigation'))"
                /></strong>
                <span>{{ navigationLabel(selectedComponent.navigation) }}</span>
              </div>
              <div class="ren-decoration-navigation-card__actions">
                <RrButton
                  size="small"
                  type="primary"
                  :disabled="
                    readonly ||
                    !templateAccess.canEditProp(selectedComponent.id, 'navigation') ||
                    linkTargets.length === 0
                  "
                  :title="lockTitle(templateAccess.canEditProp(selectedComponent.id, 'navigation'))"
                  @click="openNavigationDialog('component')">
                  选择业务页
                </RrButton>
                <RrButton
                  v-if="selectedComponent.navigation"
                  size="small"
                  :disabled="readonly || !templateAccess.canEditProp(selectedComponent.id, 'navigation')"
                  :title="lockTitle(templateAccess.canEditProp(selectedComponent.id, 'navigation'))"
                  @click="clearComponentNavigation">
                  清除
                </RrButton>
              </div>
            </header>
            <small v-if="selectedComponent.navigation?.requiredPermission"
              >客户端权限：{{ selectedComponent.navigation.requiredPermission }}</small
            >
            <small v-if="componentNavigationBindingLabel">动态参数：{{ componentNavigationBindingLabel }}</small>
          </section>
          <div v-if="widgetSettingsComponent" class="ren-decoration-items">
            <component
              :is="widgetSettingsComponent"
              :component="selectedComponent"
              :readonly="readonly"
              @update:props="applyWidgetProps"
              @pick-item-link="openItemLinkPicker" />
          </div>
          <ComponentPropsInspector
            v-if="genericPropertyFields.length"
            :component="selectedComponent"
            :fields="genericPropertyFields"
            :readonly="readonly"
            :template-bound="Boolean(templateBinding)"
            :can-edit-prop="(code) => templateAccess.canEditProp(selectedComponent.id, code)"
            :get-value="selectedPropValue"
            @change="setSelectedProp"
            @pick-item-link="openItemLinkPicker"
          />
          <section v-if="!widgetSettingsComponent && jumpItemEntries.length" class="ren-decoration-navigation-items">
            <header>
              <strong
                >{{ selectedComponent.type === 'banner' ? '轮播项跳转' : '入口项跳转' }}
                <TemplateLockMark
                  :locked="templateLocked(templateAccess.canEditProp(selectedComponent.id, 'navigation'))"
              /></strong>
              <span>每一项可以覆盖组件默认跳转</span>
            </header>
            <article v-for="entry in jumpItemEntries" :key="entry.index">
              <div>
                <strong>{{ entry.name }}</strong>
                <span>{{ entry.route || '未配置跳转' }}</span>
              </div>
              <RrButton
                size="small"
                type="default"
                :disabled="
                  readonly ||
                  !templateAccess.canEditProp(selectedComponent.id, 'navigation') ||
                  linkTargets.length === 0
                "
                :title="lockTitle(templateAccess.canEditProp(selectedComponent.id, 'navigation'))"
                @click="openNavigationDialog('item', entry.index)">
                选择
              </RrButton>
            </article>
          </section>
          <section v-if="dynamicNavigationPreviewEntries.length" class="ren-decoration-navigation-items is-preview">
            <header>
              <strong>数据源列表跳转预览</strong>
              <span>统一选择目标页，并用当前行字段生成每条独立地址</span>
            </header>
            <article v-for="entry in dynamicNavigationPreviewEntries" :key="entry.index">
              <div>
                <strong>{{ entry.name }}</strong>
                <span>{{ entry.route || '未配置，客户端使用数据项自带地址' }}</span>
              </div>
              <small>第 {{ entry.index + 1 }} 条</small>
            </article>
          </section>
        </template>

        <template v-else-if="inspectorTab === 'style'">
          <ComponentStyleInspector
            :component="selectedComponent"
            :fields="selectedStyleFields"
            :readonly="readonly"
            :template-bound="Boolean(templateBinding)"
            :can-edit-style="(code) => templateAccess.canEditStyle(selectedComponent.id, code)"
            @change="setSelectedStyle"
          />
        </template>
        <template v-else-if="inspectorTab === 'data' && showDataSourceTab">
          <ComponentDataInspector
            :source-code="selectedDataSourceCode"
            :params-text="selectedDataSourceParamsText"
            :bindings="selectedDataSourceBindings"
            :source-options="availableDataSourceOptions"
            :binding-options="targetBindingOptions"
            :active-source="activeDataSource"
            :readonly="readonly"
            :template-bound="Boolean(templateBinding)"
            :can-edit="templateAccess.canEditProp(selectedComponent.id, 'dataSource')"
            @update:source="setSelectedDataSource"
            @update:params="setSelectedDataSourceParams"
            @add-binding="addDataBinding"
            @rename-binding="renameDataBinding"
            @update-binding="setDataBindingSource"
            @remove-binding="removeDataBinding"
          />
        </template>

        <details class="ren-decoration-json">
          <summary>组件 JSON</summary>
          <pre>{{ selectedJson }}</pre>
        </details>
      </div>

      <div v-else><RrEmpty compact title="请选择预览中的组件" /></div>
    </RenDecorationInspector>
  </section>

    <RrModal
      :open="Boolean(editingTemplate && templateActionsEnabled)"
      title="编辑模板信息"
      :width="640"
      :ok-button-props="{ disabled: !templateEditForm.templateName }"
      ok-text="保存修改"
      cancel-text="取消"
      @ok="submitTemplateEditor"
      @cancel="closeTemplateEditor"
      @update:open="!$event && closeTemplateEditor()"
    >
      <RrForm layout="vertical">
        <RrFormItem label="模板名称">
          <RrInput v-model:value.trim="templateEditForm.templateName" maxlength="128" />
        </RrFormItem>
        <RrFormItem label="页面类型">
          <RrSelect v-model:value="templateEditForm.pageType" :options="targetPageTypeOptions" />
        </RrFormItem>
        <RrFormItem label="封面地址">
          <RrInput v-model:value.trim="templateEditForm.coverImageUrl" placeholder="https://..." />
        </RrFormItem>
        <RrFormItem label="模板说明">
          <RrInput v-model:value.trim="templateEditForm.description" type="textarea" :rows="4" maxlength="1024" />
        </RrFormItem>
      </RrForm>
    </RrModal>

    <RenDecorationLinkTargetDialog
      v-model:open="navigationDialogOpen"
      :link-targets="linkTargets.filter((target) => target.targetType !== 'MINI_PROGRAM' || (Array.isArray(draft.page.platforms) && draft.page.platforms.length === 1 && draft.page.platforms[0] === 'APP'))"
      :current-navigation="navigationDialogCurrent"
      :current-route-path="navigationDialogRoutePath"
      :exclude-page-code="navigationContext?.kind === 'tabbar' ? 'app' : ''"
      :auto-bind-item-params="navigationContext?.kind !== 'tabbar' && isDataDrivenListComponent(selectedComponent)"
      :infer-param-binding="inferNavigationParamBinding"
      @apply="applyNavigationTarget"
    />
</template>

<script setup lang="ts">
import {
  BadgeCheck,
  Bot,
  Box,
  Compass,
  FileText,
  Grid3X3,
  Image,
  Landmark,
  LayoutGrid,
  LockKeyhole,
  List,
  Megaphone,
  MessageCircle,
  Newspaper,
  Radio,
  Search,
  SeparatorHorizontal,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Star,
  Text,
  UserRound,
  WalletCards,
} from '@lucide/vue';
import { computed, nextTick, provide, ref, watch, type Component } from 'vue';
import { RrButton, RrEmpty, RrForm, RrFormItem, RrInput, RrModal, RrSelect, RrSwitch, type SelectOption } from '@xjlv2/ui';
import RenDecorationCanvas from './RenDecorationCanvas.vue';
import RenDecorationComponentLibrary from './RenDecorationComponentLibrary.vue';
import RenDecorationInspector from './RenDecorationInspector.vue';
import RenDecorationLinkTargetDialog from './RenDecorationLinkTargetDialog.vue';
import RenDecorationTemplateDetail from './RenDecorationTemplateDetail.vue';
import RenDecorationTemplateMarket from './RenDecorationTemplateMarket.vue';
import RenCommonComponentPreview from './RenCommonComponentPreview.vue';
import ComponentDataInspector from '../inspectors/ComponentDataInspector.vue';
import ComponentPropsInspector from '../inspectors/ComponentPropsInspector.vue';
import ComponentStyleInspector from '../inspectors/ComponentStyleInspector.vue';
import PageInspector from '../inspectors/PageInspector.vue';
import { themePresets } from '../inspectors/page-theme';

import TemplateLockMark from '../utils/TemplateLockMark';
import { createBusinessTemplateAccess } from '../utils/business-template-policy';
import {
  availableDataSourcesFor,
  hasComponentDataSourceTab,
  parseDataSourceSchema,
} from '../utils/editor-catalog';
import { parseDecorationFields } from '../utils/editor-fields';
import { isRecordListType, supportsStaticItemNavigation } from '../utils/list-component-types';
import { MINI_PROGRAM_TARGET_CODE, miniProgramRoute, isDynamicTabTarget, pageCodeFromTabRoute, type LinkTargetApplyPayload } from '../utils/link-targets';
import {
  normalizeUpperText,
  pageTypeLabel,
  templateEnabled,
  uniqueSorted,
} from '../utils/template-market';
import {
  previewBezelSize,
  previewFitScale,
  useDecorationDevice,
} from '../composables/useDecorationDevice';
import { getWidgetSettings, getWidgetTemplate, listDecorationWidgets } from '../widgets/registry';
import WidgetBox from '../widgets/WidgetBox.vue';
import { DECORATION_UPLOAD_IMAGE_KEY, uploadSaasStorageImage } from '../widgets/storage-upload';
import {
  previewBoundString,
  previewBoundValue,
  previewRecordsFor,
  recordList,
} from '../previews/preview-helpers';
import type { DecorationPageTemplateBinding } from '@xjlv2/api-client';
import type {
  RenDecorationComponent,
  RenDecorationComponentDefinition,
  RenDecorationDataSourceDefinition,
  RenDecorationFieldDefinition,
  RenDecorationLinkTargetDefinition,
  RenDecorationMarketTemplate,
  RenDecorationNavigationTarget,
  RenDecorationPaletteItem,
  RenDecorationPreviewData,
  RenDecorationSchema,
  RenDecorationTabBar,
  RenDecorationTabBarItem,
  RenDecorationThemeTokens,
} from '../../types';

defineOptions({ inheritAttrs: false });

type NavigationContext =
  | { kind: 'component' }
  | { kind: 'item'; itemIndex: number }
  | { kind: 'tabbar'; tabIndex: number };

interface RenDecorationPageOption {
  value: string;
  label: string;
  pageType?: string;
  published?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue?: RenDecorationSchema;
    shellSchema?: RenDecorationSchema;
    pageSchemas?: Record<string, RenDecorationSchema>;
    readonly?: boolean;
    title?: string;
    eyebrow?: string;
    subtitle?: string;
    moduleCode?: string;
    pageCode?: string;
    palette?: RenDecorationPaletteItem[];
    componentDefinitions?: RenDecorationComponentDefinition[];
    dataSources?: RenDecorationDataSourceDefinition[];
    linkTargets?: RenDecorationLinkTargetDefinition[];
    templates?: RenDecorationMarketTemplate[];
    pageOptions?: RenDecorationPageOption[];
    applyingTemplate?: boolean;
    previewData?: RenDecorationPreviewData;
    initialLeftTab?: 'components' | 'templates';
    templatesEnabled?: boolean;
    templateActionsEnabled?: boolean;
    templateBinding?: DecorationPageTemplateBinding | null;
    templateLockPolicyJson?: string | null;
    uploadImage?: (file: File, options?: { signal?: AbortSignal }) => Promise<string>;
  }>(),
  {
    readonly: false,
    title: '可视化装修',
    eyebrow: 'Decoration',
    subtitle: '按组件化 schema 编排客户端页面',
    moduleCode: 'mall',
    pageCode: 'home',
    palette: () => [],
    componentDefinitions: () => [],
    dataSources: () => [],
    linkTargets: () => [],
    templates: () => [],
    pageOptions: () => [],
    applyingTemplate: false,
    previewData: () => ({}),
    pageSchemas: () => ({}),
    initialLeftTab: 'components',
    templatesEnabled: true,
    templateActionsEnabled: true,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: RenDecorationSchema];
  change: [value: RenDecorationSchema];
  'apply-template': [template: RenDecorationMarketTemplate, targetPageCode?: string];
  'save-template': [schema: RenDecorationSchema];
  'refresh-templates': [];
  'open-template-recycle': [];
  'edit-template': [template: RenDecorationMarketTemplate];
  'copy-template': [template: RenDecorationMarketTemplate];
  'toggle-template-enabled': [template: RenDecorationMarketTemplate];
  'delete-template': [template: RenDecorationMarketTemplate];
  'restore-template': [template: RenDecorationMarketTemplate];
  'delete-template-permanently': [template: RenDecorationMarketTemplate];
  'select-page': [pageCode: string];
}>();

provide(DECORATION_UPLOAD_IMAGE_KEY, (file: File, options) => {
  if (props.uploadImage) return props.uploadImage(file, options);
  return uploadSaasStorageImage(file, {
    moduleCode: props.moduleCode || 'decoration',
    category: 'decoration',
    signal: options?.signal,
  });
});

function templateLocked(allowed: boolean) {
  return Boolean(props.templateBinding) && !allowed;
}

const draft = ref<RenDecorationSchema>(
  cloneSchema(props.modelValue ?? createDefaultSchema(props.moduleCode, props.pageCode)),
);
const templateAccess = computed(() =>
  createBusinessTemplateAccess(props.templateLockPolicyJson, draft.value, props.templateBinding),
);
const selectedId = ref(draft.value.components[0]?.id ?? '');
const leftTab = ref<'components' | 'templates'>(props.templatesEnabled ? props.initialLeftTab : 'components');
const inspectorTab = ref<'page' | 'component' | 'style' | 'data'>('page');
const draggingType = ref('');
const dataSourceParamsDraft = ref('');
const navigationDialogOpen = ref(false);
const navigationContext = ref<NavigationContext | null>(null);
const selectedMarketTemplateCode = ref('');
const targetPageCode = ref(props.pageCode);
const editingTemplate = ref<RenDecorationMarketTemplate | null>(null);
const templateEditForm = ref({ templateName: '', pageType: 'HOME', coverImageUrl: '', description: '' });
const previewActiveTabCode = ref('');
const { previewDeviceCode, previewDevicePresets, previewDevice, previewDeviceStyle, setScale } = useDecorationDevice();
const previewDeviceOptions = computed(() =>
  previewDevicePresets.map((device) => ({ label: device.name, value: device.code })),
);
const previewViewportRef = ref<HTMLElement | null>(null);
const previewViewportSize = ref({ width: 0, height: 0 });

watch(previewViewportRef, (el, _previous, onCleanup) => {
  if (!el || typeof ResizeObserver === 'undefined') return;
  const observer = new ResizeObserver((entries) => {
    const rect = entries[0]?.contentRect;
    if (!rect) return;
    previewViewportSize.value = { width: rect.width, height: rect.height };
  });
  observer.observe(el);
  previewViewportSize.value = { width: el.clientWidth, height: el.clientHeight };
  onCleanup(() => observer.disconnect());
});

watch([previewViewportSize, previewDevice], () => {
  setScale(
    previewFitScale(
      previewViewportSize.value.width,
      previewViewportSize.value.height,
      previewDevice.value.width,
      previewDevice.value.height,
      previewBezelSize(previewDevice.value.platform),
    ),
  );
});
const templateDialogElement = ref<HTMLElement | null>(null);
const templateDialogInitialFocus = ref<HTMLInputElement | null>(null);
let templateDialogReturnFocus: HTMLElement | null = null;

const themeColor = computed(() => draft.value.page.themeColor || '#5A46E0');
const backgroundColor = computed(() => draft.value.page.backgroundColor || '#F3F5F7');
const layoutMode = computed(() => String(draft.value.page.layoutMode || 'FREE'));
const draftHeaderMode = computed(() => String(draft.value.page.headerMode || 'HERO').toUpperCase());
const tabbarShellPage = computed(
  () => props.pageCode === 'app' || draft.value.pageCode === 'app' || layoutMode.value === 'TABBAR_SHELL',
);
const editableTabbar = computed<RenDecorationTabBar>(() => normalizedTabbar(draft.value.tabbar || draft.value.tabBar));
const editableTabbarItems = computed(() => editableTabbar.value.items);
const previewTabbar = computed<RenDecorationTabBar | undefined>(() => {
  const localTabbar = draft.value.tabbar || draft.value.tabBar;
  if (tabbarShellPage.value) return localTabbar;
  if (localTabbar?.items?.length) return localTabbar;
  return props.shellSchema?.tabbar || props.shellSchema?.tabBar;
});
const themeTokens = computed<RenDecorationThemeTokens>(() => themeTokensFor(draft.value));
const previewTabbarItems = computed(() => previewTabbar.value?.items?.filter((item) => item.code) ?? []);
const previewHasTabbar = computed(() => previewTabbarItems.value.length > 0);
const previewActiveTab = computed(
  () =>
    previewTabbarItems.value.find((item) => item.code === previewActiveTabCode.value) ?? previewTabbarItems.value[0],
);
const previewContentSchema = computed<RenDecorationSchema | null>(() => {
  if (!tabbarShellPage.value) return draft.value;
  const pageCode = previewActiveTab.value?.pageCode;
  return pageCode ? (props.pageSchemas[pageCode] ?? null) : null;
});
const shellCompositePreview = computed(() => tabbarShellPage.value && previewContentSchema.value !== draft.value);
const previewPageTitle = computed(
  () => previewContentSchema.value?.page.title || previewActiveTab.value?.text || draft.value.page.title || '页面装修',
);
const previewHeaderMode = computed(() =>
  String(previewContentSchema.value?.page.headerMode || draft.value.page.headerMode || 'HERO').toUpperCase(),
);
const previewThemeTokens = computed<RenDecorationThemeTokens>(() =>
  themeTokensFor(previewContentSchema.value ?? draft.value),
);
const previewThemeColor = computed(() => previewThemeTokens.value.primaryColor);
const previewComponents = computed(() =>
  (previewContentSchema.value?.components ?? []).filter((component) => component.enabled !== false),
);
const previewEmptyMessage = computed(() => {
  if (!tabbarShellPage.value) return '从左侧组件库添加页面内容。';
  return previewContentSchema.value
    ? `“${previewActiveTab.value?.text || '当前页面'}”尚未配置组件。`
    : `导航未找到 ${previewActiveTab.value?.pageCode || '独立页面'} 的装修配置。`;
});
const previewStageStyle = computed(() => ({
  backgroundColor: previewThemeTokens.value.backgroundColor,
  backgroundImage: previewThemeTokens.value.backgroundImage ? `url(${previewThemeTokens.value.backgroundImage})` : '',
  backgroundSize: 'cover',
  gap: `${previewThemeTokens.value.spacing}px`,
  '--ren-theme-primary': previewThemeTokens.value.primaryColor,
  '--ren-theme-accent': previewThemeTokens.value.accentColor,
  '--ren-theme-background': previewThemeTokens.value.backgroundColor,
  '--ren-theme-card': previewThemeTokens.value.cardColor,
  '--ren-theme-text': previewThemeTokens.value.textColor,
  '--ren-theme-muted': previewThemeTokens.value.mutedTextColor,
  '--ren-theme-border': previewThemeTokens.value.borderColor,
  '--ren-theme-radius': `${previewThemeTokens.value.radius}px`,
  '--ren-theme-on-primary': previewThemeTokens.value.buttonTextColor || '#FFFFFF',
  '--ren-tab-color': previewTabbar.value?.color || previewThemeTokens.value.mutedTextColor,
  '--ren-tab-active': previewTabbar.value?.selectedColor || previewThemeTokens.value.primaryColor,
  '--ren-tab-bg': previewTabbar.value?.backgroundColor || previewThemeTokens.value.cardColor,
  '--ren-tab-border': previewTabbar.value?.borderColor || previewThemeTokens.value.borderColor,
  '--ren-tab-height': `${Math.max(52, numberValue(previewTabbar.value?.height, 58))}px`,
  '--ren-tab-count': String(Math.max(1, previewTabbar.value?.items?.length ?? 1)),
}));
const enabledComponents = computed(() => draft.value.components.filter((component) => component.enabled !== false));
const previewDataSets = computed<Record<string, unknown>>(() =>
  Object.fromEntries(
    props.dataSources
      .filter((source) => source.sourceCode)
      .map((source) => [source.sourceCode, parsePreviewSample(source.sampleJson)]),
  ),
);
const selectedIndex = computed(() =>
  draft.value.components.findIndex((component) => component.id === selectedId.value),
);
const selectedComponent = computed(() => draft.value.components[selectedIndex.value] ?? null);
const jumpItemEntries = computed(() => {
  if (!selectedComponent.value || !supportsStaticItemNavigation(selectedComponent.value.type)) return [];
  return recordList(selectedComponent.value.props.items).map((item, index) => ({
    index,
    name: stringValue(item.label ?? item.title, `第 ${index + 1} 项`),
    route: navigationRoute(item.linkTarget) || stringValue(item.link, ''),
  }));
});
const dynamicNavigationPreviewEntries = computed(() => {
  const component = selectedComponent.value;
  if (!component || !isDataDrivenListComponent(component)) return [];
  const componentData = previewDataFor(component);
  return previewRecordsFor(component, componentData)
    .slice(0, 8)
    .map((item, index) => ({
      index,
      name: previewBoundString(
        component,
        componentData,
        'item.title',
        stringValue(item.title ?? item.name ?? item.label, `第 ${index + 1} 项`),
        item,
      ),
      route: previewNavigationRoute(component, item, componentData),
    }));
});
const componentNavigationTitle = computed(() =>
  isDataDrivenListComponent(selectedComponent.value) ? '列表项跳转规则' : '组件点击跳转',
);
const componentNavigationBindingLabel = computed(() => {
  const bindings = selectedComponent.value?.navigation?.paramBindings ?? {};
  return Object.entries(bindings)
    .map(([key, value]) => `${key} ← ${value}`)
    .join('，');
});
const selectedDefinition = computed(
  () => paletteItems.value.find((item) => item.type === selectedComponent.value?.type) ?? null,
);
const widgetSettingsComponent = computed(() =>
  selectedComponent.value ? getWidgetSettings(selectedComponent.value.type) : undefined,
);
const selectedPropertyFields = computed(() =>
  selectedDefinition.value?.propertyFields?.length
    ? selectedDefinition.value.propertyFields
    : fallbackFields(selectedComponent.value?.type ?? ''),
);
const genericPropertyFields = computed(() => {
  const fields = selectedPropertyFields.value;
  const type = selectedComponent.value?.type ?? '';
  if (!widgetSettingsComponent.value) return fields;
  if (type === 'news-feed' || type === 'news-topbar') {
    return fields.filter((field) => field.code !== 'channels');
  }
  return [];
});
const selectedStyleFields = computed(() => selectedDefinition.value?.styleFields ?? []);
const selectedJson = computed(() => JSON.stringify(selectedComponent.value, null, 2));
const schemaJson = computed(() => JSON.stringify(draft.value, null, 2));
const paletteItems = computed(() =>
  props.palette.length ? props.palette : paletteFromDefinitions(props.componentDefinitions),
);
const workspaceInspectorTab = computed<'data' | 'style' | 'interaction'>(() => {
  if (inspectorTab.value === 'style') return 'style';
  if (inspectorTab.value === 'data') return 'interaction';
  return 'data';
});
const selectedDataSourceCode = computed(
  () => selectedComponent.value?.dataSource?.code ?? dataSourceFromProps(selectedComponent.value)?.code ?? '',
);
const selectedDataSourceSchema = computed(() => ({
  sourceRefs: selectedDefinition.value?.dataSources ?? [],
  bindingFields: selectedDefinition.value?.dataBindingFields ?? [],
}));
const showDataSourceTab = computed(() => hasComponentDataSourceTab(selectedDataSourceSchema.value));
const selectedAvailableSources = computed(() =>
  availableDataSourcesFor(selectedDataSourceSchema.value, props.dataSources),
);
const activeDataSource = computed(
  () => props.dataSources.find((source) => source.sourceCode === selectedDataSourceCode.value) ?? null,
);
const selectedDataSourceParamsText = computed(
  () =>
    dataSourceParamsDraft.value ||
    stringifyPretty(
      selectedComponent.value?.dataSource?.params ?? dataSourceFromProps(selectedComponent.value)?.params ?? {},
    ),
);
const selectedDataSourceBindings = computed(() => selectedComponent.value?.dataSource?.bindings ?? {});
const dataBindingEntries = computed(() => Object.entries(selectedDataSourceBindings.value));
const bindingTargetOptions = computed(() =>
  selectedDataSourceSchema.value.bindingFields.map((field) => ({
    label: field.label,
    value: field.code,
  })),
);
const targetBindingOptions = computed<SelectOption[]>(() =>
  bindingTargetOptions.value.map((opt) => ({
    label: opt.label,
    value: opt.value,
  })),
);
const availableDataSourceOptions = computed<SelectOption[]>(() => [
  { label: '静态配置', value: '' },
  ...selectedAvailableSources.value.map((source) => ({
    label: source.sourceName,
    value: source.sourceCode,
  })),
]);
const navigationDialogCurrent = computed(() => {
  const context = navigationContext.value;
  if (!context || context.kind === 'tabbar') return null;
  return context.kind === 'component' ? selectedComponent.value?.navigation ?? null : itemNavigation(context.itemIndex);
});
const navigationDialogRoutePath = computed(() => {
  const context = navigationContext.value;
  if (context?.kind !== 'tabbar') return '';
  return editableTabbar.value.items[context.tabIndex]?.routePath || '';
});
const targetPageTypes = computed(() =>
  uniqueSorted([
    'HOME',
    'SHOP_HOME',
    'DETAIL',
    'ACTIVITY',
    ...props.templates.map((template) => normalizeUpperText(template.pageType)).filter(Boolean),
  ]),
);
const targetPageTypeOptions = computed(() =>
  targetPageTypes.value.map((type) => ({ value: type, label: pageTypeLabel(type) }))
);
const selectedMarketTemplate = computed(
  () => props.templates.find((template) => template.templateCode === selectedMarketTemplateCode.value) ?? null,
);
const targetPageOptions = computed(() => {
  const suggested = suggestedTargetPageCode(selectedMarketTemplate.value?.pageType);
  const options = props.pageOptions
    .filter((option) => option.value && normalizeUpperText(option.pageType) !== 'TABBAR_SHELL')
    .map((option) => ({
      value: option.value,
      label: `${option.label} (${option.value})${option.published ? '' : ' · 草稿'}`,
    }));
  const existingValues = new Set(options.map((option) => option.value));
  const createCandidates = uniqueSorted(
    [suggested, props.pageCode, 'home', 'product-detail', 'shop-home', 'topic'].filter(Boolean),
  );
  for (const value of createCandidates) {
    if (existingValues.has(value)) continue;
    options.push({ value, label: `新建：${pageCodeLabel(value)} (${value})` });
  }
  return options;
});
watch(
  () => props.modelValue,
  (value) => {
    draft.value = cloneSchema(value ?? createDefaultSchema(props.moduleCode, props.pageCode));
    syncPreviewActiveTab();
    if (!draft.value.components.some((component) => component.id === selectedId.value)) {
      selectedId.value = draft.value.components[0]?.id ?? '';
    }
  },
  { deep: true },
);

watch(
  () => [props.pageCode, props.shellSchema, props.pageSchemas, previewTabbar.value] as const,
  () => syncPreviewActiveTab(),
  { deep: true, immediate: true },
);

watch(selectedComponent, () => {
  dataSourceParamsDraft.value = '';
});

watch(showDataSourceTab, (visible) => {
  if (!visible && inspectorTab.value === 'data') inspectorTab.value = 'component';
});

watch(
  () => [props.initialLeftTab, props.templatesEnabled] as const,
  ([value, enabled]) => {
    leftTab.value = enabled ? value : 'components';
  },
);

watch(editingTemplate, (value) => {
  if (value) {
    templateDialogReturnFocus = activeHtmlElement();
    void focusDialogAfterRender(templateDialogElement, templateDialogInitialFocus);
    return;
  }
  restoreDialogFocus(templateDialogReturnFocus);
  templateDialogReturnFocus = null;
});

function paletteFromDefinitions(definitions: RenDecorationComponentDefinition[]) {
  if (!definitions.length) return defaultPalette();
  return definitions
    .filter((definition) => definition.enabled !== false)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map<RenDecorationPaletteItem>((definition) => {
      const dataSourceSchema = parseDataSourceSchema(definition.dataSourceSchemaJson);
      return {
        type: definition.type,
        label: definition.label,
        description: definition.description,
        group: definition.groupName,
        icon: definition.icon,
        defaults: parseRecord(definition.defaultPropsJson),
        propertyFields: parseFields(definition.propertySchemaJson),
        styleFields: parseFields(definition.styleSchemaJson),
        dataSources: dataSourceSchema.sourceRefs,
        dataBindingFields: dataSourceSchema.bindingFields,
        componentScope: definition.componentScope,
        ownerModuleCode: definition.ownerModuleCode,
        supportedPlatforms: parseStringArray(definition.supportedPlatformsJson),
        supportedPlatformsJson: definition.supportedPlatformsJson,
        protocolVersion: definition.protocolVersion,
      };
    });
}

function defaultPalette(): RenDecorationPaletteItem[] {
  const common = listDecorationWidgets().map((widget) => ({
    type: widget.type,
    label: widget.name,
    description: widget.description,
    group: widget.group,
    icon: widget.icon,
    defaults: widget.defaultProps ?? {},
  }));
  return common;
}

function fallbackFields(_type: string): RenDecorationFieldDefinition[] {
  return [];
}

function defaultTitleFor(moduleCode: string, pageCode: string): string {
  const code = (moduleCode || '').trim().toLowerCase();
  if (code === 'cms') {
    switch (pageCode) {
      case 'feed':
        return '站点信息流';
      case 'detail':
        return '内容详情页';
      case 'list':
        return '栏目列表页';
      case 'sites':
        return '站点选择页';
      default:
        return 'CMS首页';
    }
  }
  if (code === 'content') return '内容平台首页';
  if (code === 'guoxue') return '国学首页';
  if (code === 'mall' || code === 'decoration' || !code) return '商城首页';
  return `${moduleCode}首页`;
}

function createDefaultSchema(moduleCode: string, pageCode: string): RenDecorationSchema {
  return {
    version: '2.0',
    moduleCode,
    pageCode,
    page: {
      title: defaultTitleFor(moduleCode, pageCode),
      layoutMode: 'FREE',
      themePreset: 'brand',
      themeColor: '#5A46E0',
      accentColor: '#4B39C7',
      backgroundColor: '#F3F5F7',
      cardColor: '#FFFFFF',
      textColor: '#171A1F',
      mutedTextColor: '#67717F',
      buttonTextColor: '#FFFFFF',
      borderColor: '#E1E5EA',
      radius: 8,
      spacing: 16,
    },
    components: [],
  };
}

function cloneSchema(schema: RenDecorationSchema): RenDecorationSchema {
  return JSON.parse(JSON.stringify(schema)) as RenDecorationSchema;
}

function themeTokensFor(schema: RenDecorationSchema): RenDecorationThemeTokens {
  const page = schema.page;
  const brand = themePresets.find((item) => item.code === 'brand');
  const primaryColor = stringValue(page.themeColor, brand?.primaryColor || '#5A46E0');
  return {
    primaryColor,
    accentColor: stringValue(page.accentColor, brand?.accentColor || primaryColor),
    backgroundColor: stringValue(page.backgroundColor, brand?.backgroundColor || '#F3F5F7'),
    backgroundImage: stringValue(page.backgroundImage, ''),
    cardColor: stringValue(page.cardColor, brand?.cardColor || '#FFFFFF'),
    textColor: stringValue(page.textColor, brand?.textColor || '#171A1F'),
    mutedTextColor: stringValue(page.mutedTextColor, brand?.mutedTextColor || '#67717F'),
    buttonTextColor: stringValue(page.buttonTextColor, brand?.buttonTextColor || '#FFFFFF'),
    borderColor: stringValue(page.borderColor, brand?.borderColor || '#E1E5EA'),
    radius: numberValue(page.radius, 8),
    spacing: numberValue(page.spacing, 16),
  };
}

function createId(type: string) {
  return `${type}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function commit(nextSchema: RenDecorationSchema) {
  draft.value = nextSchema;
  emit('update:modelValue', cloneSchema(nextSchema));
  emit('change', cloneSchema(nextSchema));
}

function addComponent(item: RenDecorationPaletteItem) {
  if (!templateAccess.value.canAdd()) return;
  const defaults = cloneRecord(item.defaults);
  const embeddedSource = dataSourceFromProps({ props: defaults } as RenDecorationComponent);
  const component: RenDecorationComponent = {
    id: createId(item.type),
    type: item.type,
    title: item.label,
    enabled: true,
    dataSource: embeddedSource,
    props: withoutDataSource(defaults),
    style: {},
  };
  commit({ ...draft.value, components: [...draft.value.components, component] });
  selectedId.value = component.id;
  inspectorTab.value = 'component';
}

function startDrag(type: string) {
  draggingType.value = type;
}

function dropComponent() {
  if (!draggingType.value || props.readonly) return;
  const item = paletteItems.value.find((entry) => entry.type === draggingType.value);
  draggingType.value = '';
  if (item) addComponent(item);
}

function selectComponent(id: string) {
  selectedId.value = id;
  if (inspectorTab.value === 'page') inspectorTab.value = 'component';
}

function selectPreviewComponent(id: string) {
  if (shellCompositePreview.value) return;
  selectComponent(id);
}

function setInspectorContext(context: 'page' | 'component') {
  inspectorTab.value = context === 'page' ? 'page' : 'component';
}

function setWorkspaceInspectorTab(tab: 'data' | 'style' | 'interaction') {
  inspectorTab.value = tab === 'style' ? 'style' : tab === 'interaction' ? 'data' : 'component';
}

function setPageTitle(title: string) {
  if (!templateAccess.value.canEditPage('title')) return;
  commit({ ...draft.value, page: { ...draft.value.page, title } });
}

function setPageValue(key: string, value: unknown) {
  if (!templateAccess.value.canEditPage(key)) return;
  commit({ ...draft.value, page: { ...draft.value.page, [key]: value } });
}

function setPageColor(
  key:
    | 'themeColor'
    | 'accentColor'
    | 'backgroundColor'
    | 'cardColor'
    | 'textColor'
    | 'mutedTextColor'
    | 'buttonTextColor'
    | 'borderColor',
  value: string,
) {
  setPageValue(key, value);
}

function setPageNumber(key: 'radius' | 'spacing', value: string) {
  setPageValue(key, Number(value || 0));
}

function normalizedTabbar(value?: RenDecorationTabBar): RenDecorationTabBar {
  return {
    mode: value?.mode || 'DYNAMIC',
    position: value?.position || 'bottom',
    color: value?.color || themeTokens.value.mutedTextColor,
    selectedColor: value?.selectedColor || themeTokens.value.primaryColor,
    backgroundColor: value?.backgroundColor || themeTokens.value.cardColor,
    borderColor: value?.borderColor || themeTokens.value.borderColor,
    height: Math.max(52, numberValue(value?.height, 58)),
    prominentCode: value?.prominentCode || '',
    items: value?.items?.map((item) => ({ ...item })) ?? [],
  };
}

function commitTabbar(tabbar: RenDecorationTabBar) {
  if (!templateAccess.value.canEditPage('tabbar')) return;
  commit({ ...draft.value, tabbar });
}

function setTabbarValue<K extends keyof RenDecorationTabBar>(key: K, value: RenDecorationTabBar[K]) {
  commitTabbar({ ...editableTabbar.value, [key]: value });
}

function setTabbarItemValue<K extends keyof RenDecorationTabBarItem>(
  index: number,
  key: K,
  value: RenDecorationTabBarItem[K],
) {
  const tabbar = editableTabbar.value;
  const items = tabbar.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item));
  commitTabbar({ ...tabbar, items });
}

function setTabbarItemCode(index: number, rawCode: string) {
  const tabbar = editableTabbar.value;
  const current = tabbar.items[index];
  if (!current) return;
  const code = uniqueTabbarCode(normalizeTabbarCode(rawCode) || `tab-${index + 1}`, index);
  const items = tabbar.items.map((item, itemIndex) => (itemIndex === index ? { ...item, code } : item));
  commitTabbar({
    ...tabbar,
    prominentCode: tabbar.prominentCode === current.code ? code : tabbar.prominentCode,
    items,
  });
  if (previewActiveTabCode.value === current.code) previewActiveTabCode.value = code;
}

function addTabbarItem() {
  const tabbar = editableTabbar.value;
  if (tabbar.items.length >= 5) return;
  const usedPageCodes = new Set(tabbar.items.map((item) => item.pageCode).filter(Boolean));
  const target = props.linkTargets
    .filter((item) => item.enabled !== false && item.pageCode !== 'app')
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .find((item) => !usedPageCodes.has(item.pageCode));
  const codeSeed = target?.pageCode || target?.targetCode || `tab-${tabbar.items.length + 1}`;
  const code = uniqueTabbarCode(normalizeTabbarCode(codeSeed), -1);
  const pageTab = target ? isDynamicTabTarget(target) : false;
  const item: RenDecorationTabBarItem = {
    code,
    text: target?.targetName?.slice(0, 6) || '新导航',
    pageCode: pageTab ? target?.pageCode || '' : '',
    icon: 'circle',
    activeIcon: 'circle-filled',
    routePath: target?.routePath || '',
    loginRequired: target?.loginRequired || false,
    action: target ? !pageTab : false,
    prominent: false,
  };
  commitTabbar({ ...tabbar, items: [...tabbar.items, item] });
  syncPreviewActiveTab();
}

function moveTabbarItem(index: number, offset: -1 | 1) {
  const tabbar = editableTabbar.value;
  const targetIndex = index + offset;
  if (index < 0 || targetIndex < 0 || targetIndex >= tabbar.items.length) return;
  const items = [...tabbar.items];
  [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
  commitTabbar({ ...tabbar, items });
}

function removeTabbarItem(index: number) {
  const tabbar = editableTabbar.value;
  if (tabbar.items.length <= 2) return;
  const removed = tabbar.items[index];
  const items = tabbar.items.filter((_, itemIndex) => itemIndex !== index);
  commitTabbar({
    ...tabbar,
    prominentCode: removed?.code === tabbar.prominentCode ? '' : tabbar.prominentCode,
    items,
  });
  syncPreviewActiveTab();
}

function setTabbarItemAction(index: number, action: boolean) {
  const tabbar = editableTabbar.value;
  const items = tabbar.items.map((item, itemIndex) => {
    if (itemIndex !== index) return item;
    const pageCode = item.pageCode || pageCodeFromTabRoute(item.routePath || '');
    return {
      ...item,
      action: action || !pageCode,
      pageCode: action ? '' : pageCode,
    };
  });
  commitTabbar({ ...tabbar, items });
}

function setTabbarProminent(index: number, prominent: boolean) {
  const tabbar = editableTabbar.value;
  const selected = tabbar.items[index];
  if (!selected) return;
  const items = tabbar.items.map((item, itemIndex) => ({ ...item, prominent: prominent && itemIndex === index }));
  commitTabbar({ ...tabbar, prominentCode: prominent ? selected.code : '', items });
}

function uniqueTabbarCode(seed: string, currentIndex: number) {
  const base = seed || 'tab';
  const used = new Set(
    editableTabbar.value.items.filter((_, index) => index !== currentIndex).map((item) => item.code),
  );
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
}

function normalizeTabbarCode(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function applyThemePreset(code: string) {
  const preset = themePresets.find((item) => item.code === code);
  if (!preset) return;
  if (
    ![
      'themePreset',
      'themeColor',
      'accentColor',
      'backgroundColor',
      'backgroundImage',
      'cardColor',
      'textColor',
      'mutedTextColor',
      'buttonTextColor',
      'borderColor',
      'headerMode',
    ].every((field) => templateAccess.value.canEditPage(field))
  )
    return;
  commit({
    ...draft.value,
    page: {
      ...draft.value.page,
      themePreset: preset.code,
      themeColor: preset.primaryColor,
      accentColor: preset.accentColor,
      backgroundColor: preset.backgroundColor,
      backgroundImage: preset.backgroundImage,
      cardColor: preset.cardColor,
      textColor: preset.textColor,
      mutedTextColor: preset.mutedTextColor,
      buttonTextColor: preset.buttonTextColor,
      borderColor: preset.borderColor,
      ...(preset.headerMode ? { headerMode: preset.headerMode } : {}),
    },
  });
}

const canApplyThemePreset = computed(() =>
  [
    'themePreset',
    'themeColor',
    'accentColor',
    'backgroundColor',
    'backgroundImage',
    'cardColor',
    'textColor',
    'mutedTextColor',
    'buttonTextColor',
    'borderColor',
    'headerMode',
  ].every((field) => templateAccess.value.canEditPage(field)),
);

function lockTitle(allowed: boolean, fallback = '') {
  return allowed || !props.templateBinding ? fallback : '该字段由业务模板锁定';
}

function previewActionLocked(componentId: string, action: 'move' | 'remove') {
  if (!props.templateBinding) return false;
  return action === 'move' ? !templateAccess.value.canMove(componentId) : !templateAccess.value.canRemove(componentId);
}

function previewActionTitle(componentId: string, action: 'move' | 'remove', fallback: string) {
  return previewActionLocked(componentId, action) ? '该操作由业务模板锁定' : fallback;
}

function setSelectedEnabled(enabled: boolean) {
  if (!selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, 'enabled')) return;
  updateSelected((component) => ({ ...component, enabled }));
}

function setSelectedProp(key: string, value: unknown) {
  if (!selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, key)) return;
  updateSelected((component) => ({ ...component, props: { ...component.props, [key]: value } }));
}

function applyWidgetProps(nextProps: Record<string, unknown>) {
  if (!selectedComponent.value) return;
  const current = selectedComponent.value;
  const merged = { ...(current.props ?? {}) };
  for (const [key, value] of Object.entries(nextProps)) {
    if (templateAccess.value.canEditProp(current.id, key)) {
      merged[key] = value;
    }
  }
  updateSelected((component) => ({ ...component, props: merged }));
}

function openItemLinkPicker(index: number) {
  if (!selectedComponent.value) return;
  if (!templateAccess.value.canEditProp(selectedComponent.value.id, 'navigation')) return;
  openNavigationDialog('item', index);
}

function setSelectedStyle(key: string, value: unknown) {
  if (!selectedComponent.value || !templateAccess.value.canEditStyle(selectedComponent.value.id, key)) return;
  updateSelected((component) => ({ ...component, style: { ...(component.style ?? {}), [key]: value } }));
}

function updateSelected(updater: (component: RenDecorationComponent) => RenDecorationComponent) {
  if (selectedIndex.value < 0) return;
  commit({
    ...draft.value,
    components: draft.value.components.map((component, index) =>
      index === selectedIndex.value ? updater(component) : component,
    ),
  });
}


function canMovePreviewComponent(id: string, delta: -1 | 1) {
  if (draft.value.components.find((component) => component.id === id)?.locked || !templateAccess.value.canMove(id))
    return false;
  const visibleIndex = enabledComponents.value.findIndex((component) => component.id === id);
  const targetIndex = visibleIndex + delta;
  return visibleIndex >= 0 && targetIndex >= 0 && targetIndex < enabledComponents.value.length;
}

function movePreviewComponent(id: string, delta: -1 | 1) {
  if (!canMovePreviewComponent(id, delta)) return;
  const visibleIndex = enabledComponents.value.findIndex((component) => component.id === id);
  const target = enabledComponents.value[visibleIndex + delta];
  const from = draft.value.components.findIndex((component) => component.id === id);
  const to = draft.value.components.findIndex((component) => component.id === target.id);
  if (from < 0 || to < 0) return;
  const components = [...draft.value.components];
  const [component] = components.splice(from, 1);
  components.splice(to, 0, component);
  commit({ ...draft.value, components });
  selectedId.value = id;
}

function duplicateComponent() {
  if (!selectedComponent.value || selectedComponent.value.locked || !templateAccess.value.canAdd()) return;
  const copy = cloneComponent(selectedComponent.value);
  copy.id = createId(copy.type);
  copy.title = `${copy.title} 副本`;
  const components = [...draft.value.components];
  components.splice(selectedIndex.value + 1, 0, copy);
  commit({ ...draft.value, components });
  selectedId.value = copy.id;
}


function removeComponentById(id: string) {
  const index = draft.value.components.findIndex((component) => component.id === id);
  if (index < 0) return;
  if (draft.value.components[index]?.required || !templateAccess.value.canRemove(id)) return;
  const components = draft.value.components.filter((component) => component.id !== id);
  commit({ ...draft.value, components });
  selectedId.value = components[Math.min(index, components.length - 1)]?.id ?? '';
}

const legacyNewsFeedPropDefaults: Record<string, unknown> = {
  pageSize: 10,
  layoutMode: 'LIST',
  columnCount: 2,
  mainAxisSpacing: 10,
  crossAxisSpacing: 10,
  showSummary: true,
  showCover: true,
  showSource: true,
  showPublishedAt: true,
  showComments: false,
  showRefreshTip: false,
  compactMode: false,
};

function selectedPropValue(code: string) {
  const component = selectedComponent.value;
  if (!component) return undefined;
  if (Object.prototype.hasOwnProperty.call(component.props, code)) return component.props[code];
  if (component.type === 'news-feed' && Object.prototype.hasOwnProperty.call(legacyNewsFeedPropDefaults, code)) {
    return legacyNewsFeedPropDefaults[code];
  }
  return undefined;
}

function setSelectedDataSource(code: string) {
  if (!selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, 'dataSource')) return;
  updateSelected((component) => ({
    ...component,
    dataSource: code ? { code, params: {}, bindings: component.dataSource?.bindings ?? {} } : null,
  }));
  dataSourceParamsDraft.value = '';
}

function setSelectedDataSourceParams(value: string) {
  if (!selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, 'dataSource')) return;
  dataSourceParamsDraft.value = value;
  try {
    const params = JSON.parse(value) as Record<string, unknown>;
    updateSelected((component) => ({
      ...component,
      dataSource: selectedDataSourceCode.value
        ? { code: selectedDataSourceCode.value, params, bindings: component.dataSource?.bindings ?? {} }
        : null,
    }));
  } catch {
    // 用户正在输入未完成 JSON 时先保留文本，不破坏已保存参数。
  }
}

function addDataBinding() {
  const existing = selectedDataSourceBindings.value;
  const target =
    bindingTargetOptions.value.find((option) => !(option.value in existing))?.value ??
    `field${Object.keys(existing).length + 1}`;
  updateDataBindings({ ...existing, [target]: '' });
}

function renameDataBinding(currentTarget: string, nextTarget: string) {
  if (!nextTarget || nextTarget === currentTarget) return;
  const bindings = { ...selectedDataSourceBindings.value };
  const source = bindings[currentTarget] ?? '';
  delete bindings[currentTarget];
  bindings[nextTarget] = source;
  updateDataBindings(bindings);
}

function setDataBindingSource(target: string, source: string) {
  updateDataBindings({ ...selectedDataSourceBindings.value, [target]: source });
}

function removeDataBinding(target: string) {
  const bindings = { ...selectedDataSourceBindings.value };
  delete bindings[target];
  updateDataBindings(bindings);
}

function updateDataBindings(bindings: Record<string, string>) {
  if (!selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, 'dataSource')) return;
  updateSelected((component) => ({
    ...component,
    dataSource: component.dataSource ? { ...component.dataSource, bindings } : null,
  }));
}

function openNavigationDialog(kind: 'component' | 'item', itemIndex?: number) {
  if (!selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, 'navigation')) return;
  navigationContext.value = kind === 'component' ? { kind: 'component' } : { kind: 'item', itemIndex: itemIndex ?? -1 };
  navigationDialogOpen.value = true;
}

function openTabbarNavigationDialog(tabIndex: number) {
  if (!templateAccess.value.canEditPage('tabbar')) return;
  navigationContext.value = { kind: 'tabbar', tabIndex };
  navigationDialogOpen.value = true;
}

function openTabbarPageEditor(item: RenDecorationTabBarItem) {
  if (item.action || !item.pageCode) return;
  emit('select-page', item.pageCode);
}

function closeNavigationDialog() {
  navigationDialogOpen.value = false;
  navigationContext.value = null;
}

async function focusDialogAfterRender<T extends HTMLElement>(
  dialogRef: { value: HTMLElement | null },
  initialFocusRef: { value: T | null },
) {
  await nextTick();
  const dialog = dialogRef.value;
  if (!dialog) return;
  const target = initialFocusRef.value ?? dialogFocusableElements(dialog)[0] ?? dialog;
  target.focus();
}

function restoreDialogFocus(target: HTMLElement | null) {
  if (!target) return;
  void nextTick(() => {
    if (target.isConnected) target.focus();
  });
}

function activeHtmlElement() {
  return typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
}

function dialogFocusableElements(dialog: HTMLElement) {
  return Array.from(
    dialog.querySelectorAll<HTMLElement>(
      [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(','),
    ),
  ).filter((element) => element.tabIndex >= 0 && !element.hidden);
}

function applyNavigationTarget(payload: LinkTargetApplyPayload) {
  const context = navigationContext.value;
  if (!payload?.target || !context) return;
  if (
    context.kind === 'tabbar'
      ? !templateAccess.value.canEditPage('tabbar')
      : !selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, 'navigation')
  )
    return;
  const { target } = payload;
  const navigation = navigationFromTarget(target, payload.params, payload.paramBindings);
  if (context.kind === 'component') {
    updateSelected((component) => ({
      ...component,
      navigation,
      props: { ...component.props, link: navigation.routePath },
    }));
  } else if (context.kind === 'item') {
    updateSelected((component) => {
      const items = recordList(component.props.items).map((item, index) =>
        index === context.itemIndex ? { ...item, link: navigation.routePath, linkTarget: navigation } : item,
      );
      return { ...component, props: { ...component.props, items } };
    });
  } else {
    const tabbar = editableTabbar.value;
    const current = tabbar.items[context.tabIndex];
    if (!current) return;
    const pageTab = isDynamicTabTarget(target);
    const items = tabbar.items.map((item, index) =>
      index === context.tabIndex
        ? {
            ...item,
            text: item.text === '新导航' || !item.text ? target.targetName.slice(0, 6) : item.text,
            pageCode: pageTab ? target.pageCode : '',
            routePath: navigation.routePath,
            loginRequired: target.loginRequired,
            action: !pageTab,
          }
        : item,
    );
    commitTabbar({ ...tabbar, items });
  }
  closeNavigationDialog();
}

function clearComponentNavigation() {
  if (!selectedComponent.value || !templateAccess.value.canEditProp(selectedComponent.value.id, 'navigation')) return;
  updateSelected((component) => ({
    ...component,
    navigation: null,
    props: { ...component.props, link: '' },
  }));
}

function navigationFromTarget(
  target: RenDecorationLinkTargetDefinition,
  params: Record<string, string | number | boolean>,
  paramBindings: Record<string, string>,
): RenDecorationNavigationTarget {
  const activeBindings = Object.fromEntries(Object.entries(paramBindings).filter(([, value]) => value.trim()));
  return {
    targetCode: target.targetCode,
    targetName: target.targetName,
    moduleCode: target.moduleCode,
    targetType: target.targetType,
    pageCode: target.pageCode,
    routePath: target.targetCode === MINI_PROGRAM_TARGET_CODE ? miniProgramRoute(params) : interpolateRoute(target.routePath, params),
    routeTemplate: target.targetCode === MINI_PROGRAM_TARGET_CODE ? miniProgramRoute(params) : target.routePath,
    params,
    paramBindings: activeBindings,
    requiredPermission: target.requiredPermission,
    loginRequired: target.loginRequired,
  };
}

function interpolateRoute(routePath: string, params: Record<string, string | number | boolean>) {
  return Object.entries(params).reduce(
    (route, [key, value]) =>
      value === '' || value == null ? route : route.replaceAll(`{${key}}`, encodeURIComponent(String(value))),
    routePath,
  );
}

function navigationLabel(navigation?: RenDecorationNavigationTarget | null) {
  return navigation
    ? `${navigation.targetName || navigation.targetCode} · ${navigation.routePath}`
    : '未配置，按组件自身行为处理';
}

function navigationRoute(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  return stringValue((value as Record<string, unknown>).routePath, '');
}

function itemNavigation(index: number) {
  const item = recordList(selectedComponent.value?.props.items)[index];
  const value = item?.linkTarget;
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as unknown as RenDecorationNavigationTarget)
    : null;
}

function isDataDrivenListComponent(component: RenDecorationComponent | null | undefined) {
  const sourceCode = component?.dataSource?.code ?? dataSourceFromProps(component)?.code ?? '';
  if (!component || !sourceCode) return false;
  return isRecordListType(component.type);
}

function inferNavigationParamBinding(paramCode: string) {
  const component = selectedComponent.value;
  if (!component) return `item.${paramCode}`;
  const records = previewRecordsFor(component, previewDataFor(component));
  const sample = records[0] ?? {};
  const aliases: Record<string, string[]> = {
    roomId: ['roomId', 'id'],
    expertId: ['expertId', 'id'],
    productId: ['productId', 'id'],
    articleId: ['articleId', 'id'],
    orderId: ['orderId', 'id'],
    toolCode: ['toolCode', 'code'],
  };
  const aliasFields = aliases[paramCode] ?? [];
  const field = [paramCode, ...aliasFields, 'routeId', 'id'].find(
    (candidate) => sample[candidate] !== undefined && sample[candidate] !== null,
  );
  return `item.${field || aliasFields[aliasFields.length - 1] || paramCode}`;
}

function previewNavigationRoute(
  component: RenDecorationComponent,
  item: Record<string, unknown>,
  componentData: unknown,
) {
  const itemTarget = item.linkTarget;
  const target =
    itemTarget && typeof itemTarget === 'object' && !Array.isArray(itemTarget)
      ? (itemTarget as unknown as RenDecorationNavigationTarget)
      : component.navigation;
  if (!target) return stringValue(item.link ?? item.routePath, '');
  const params: Record<string, string | number | boolean> = { ...(target.params ?? {}) };
  for (const [key, binding] of Object.entries(target.paramBindings ?? {})) {
    const value = previewBoundValue(component, componentData, binding, undefined, item);
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') params[key] = value;
  }
  return interpolateRoute(target.routeTemplate || target.routePath, params);
}

function parseRecord(raw: string) {
  try {
    const parsed = JSON.parse(raw || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function parseFields(raw: string): RenDecorationFieldDefinition[] {
  return parseDecorationFields(raw);
}

function parseStringArray(raw?: string) {
  try {
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed.map((item) => String(item).toUpperCase()) : [];
  } catch {
    return [];
  }
}

function cloneRecord(record: Record<string, unknown>) {
  return JSON.parse(JSON.stringify(record)) as Record<string, unknown>;
}

function cloneComponent(component: RenDecorationComponent) {
  return JSON.parse(JSON.stringify(component)) as RenDecorationComponent;
}

function dataSourceFromProps(component?: Pick<RenDecorationComponent, 'props'> | null) {
  const value = component?.props?.dataSource;
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as { code: string; params?: Record<string, unknown>; bindings?: Record<string, string> })
    : null;
}

function withoutDataSource(props: Record<string, unknown>) {
  const next = { ...props };
  delete next.dataSource;
  return next;
}

function stringifyPretty(value: unknown) {
  return JSON.stringify(value ?? {}, null, 2);
}

function openTemplateDetail(templateCode: string) {
  selectedMarketTemplateCode.value = templateCode;
  const template = props.templates.find((item) => item.templateCode === templateCode);
  targetPageCode.value = suggestedTargetPageCode(template?.pageType);
}

function closeTemplateDetail() {
  selectedMarketTemplateCode.value = '';
}

function editTemplate(template: RenDecorationMarketTemplate) {
  if (props.readonly || !props.templateActionsEnabled || template.publicTemplate) return;
  editingTemplate.value = template;
  templateEditForm.value = {
    templateName: template.templateName,
    pageType: normalizeUpperText(template.pageType) || 'HOME',
    coverImageUrl: template.coverImageUrl || '',
    description: template.description || '',
  };
}

function copyTemplate(template: RenDecorationMarketTemplate) {
  if (props.readonly || !props.templateActionsEnabled) return;
  emit('copy-template', template);
}

function toggleTemplateEnabled(template: RenDecorationMarketTemplate) {
  if (props.readonly || !props.templateActionsEnabled || template.publicTemplate) return;
  emit('toggle-template-enabled', template);
}

function deleteTemplate(template: RenDecorationMarketTemplate) {
  if (
    props.readonly ||
    !props.templateActionsEnabled ||
    template.publicTemplate ||
    !confirmTemplateAction(`确定把“${template.templateName}”移入回收站吗？`)
  )
    return;
  emit('delete-template', template);
}

function restoreTemplate(template: RenDecorationMarketTemplate) {
  if (props.readonly || !props.templateActionsEnabled || template.publicTemplate) return;
  emit('restore-template', template);
}

function deleteTemplatePermanently(template: RenDecorationMarketTemplate) {
  if (
    props.readonly ||
    !props.templateActionsEnabled ||
    template.publicTemplate ||
    !confirmTemplateAction(`永久删除“${template.templateName}”后无法恢复，确定继续吗？`)
  )
    return;
  emit('delete-template-permanently', template);
}

function submitTemplateEditor() {
  if (props.readonly || !props.templateActionsEnabled || !editingTemplate.value || !templateEditForm.value.templateName)
    return;
  emit('edit-template', {
    ...editingTemplate.value,
    templateName: templateEditForm.value.templateName,
    pageType: templateEditForm.value.pageType,
    coverImageUrl: templateEditForm.value.coverImageUrl,
    description: templateEditForm.value.description,
  });
  closeTemplateEditor();
}

function closeTemplateEditor() {
  editingTemplate.value = null;
}

function applyTemplateFromMarket(template: RenDecorationMarketTemplate, pageCode?: string) {
  if (pageCode) targetPageCode.value = pageCode;
  if (props.readonly || !props.templateActionsEnabled || props.applyingTemplate || !templateEnabled(template)) return;
  emit('apply-template', template, targetPageCode.value);
}

function confirmTemplateAction(message: string) {
  return typeof window === 'undefined' || window.confirm(message);
}

function suggestedTargetPageCode(pageType?: string) {
  const normalized = normalizeUpperText(pageType || '');
  if (normalized === 'DETAIL') return 'product-detail';
  if (normalized === 'SHOP_HOME') return 'shop-home';
  if (normalized === 'ACTIVITY') return 'topic';
  return props.pageCode || 'home';
}

function pageCodeLabel(code: string) {
  const labels: Record<string, string> = {
    home: '产品首页',
    'product-detail': '商品详情页',
    'shop-home': '店铺首页',
    topic: '专题活动页',
  };
  return labels[code] ?? '自定义页面';
}

function parsePreviewSample(raw: string) {
  if (!raw?.trim()) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function previewDataFor(component: RenDecorationComponent) {
  const code = component.dataSource?.code ?? dataSourceFromProps(component)?.code ?? '';
  return code ? previewDataSets.value[code] : null;
}

function selectPreviewTab(item: RenDecorationTabBarItem) {
  if (item.action || !item.pageCode) return;
  if (tabbarShellPage.value) {
    previewActiveTabCode.value = item.code;
    return;
  }
  emit('select-page', item.pageCode);
}

function paletteIconComponent(item: RenDecorationPaletteItem): Component {
  const icon = stringValue(item.icon, item.type).trim().toLowerCase();
  const icons: Record<string, Component> = {
    'badge-check': BadgeCheck,
    bot: Bot,
    compass: Compass,
    file: FileText,
    'file-text': FileText,
    grid: Grid3X3,
    image: Image,
    landmark: Landmark,
    'layout-grid': LayoutGrid,
    list: List,
    megaphone: Megaphone,
    message: MessageCircle,
    'message-circle': MessageCircle,
    newspaper: Newspaper,
    radio: Radio,
    search: Search,
    separator: SeparatorHorizontal,
    shopping: ShoppingBag,
    'shopping-bag': ShoppingBag,
    sparkles: Sparkles,
    star: Star,
    text: Text,
    user: UserRound,
    wallet: WalletCards,
  };
  return icons[icon] ?? icons[item.type.toLowerCase()] ?? Box;
}

function syncPreviewActiveTab() {
  const items = previewTabbarItems.value;
  const pageCode = props.pageCode || draft.value.pageCode;
  const active =
    items.find((item) => item.pageCode === pageCode) ??
    items.find((item) => item.code === previewActiveTabCode.value) ??
    items[0];
  previewActiveTabCode.value = active?.code ?? '';
}

function previewTabIcon(item: RenDecorationTabBarItem) {
  const icon = stringValue(item.icon, item.text).toLowerCase();
  const mapped: Record<string, string> = {
    home: '⌂',
    'home-filled': '⌂',
    book: '▤',
    'book-filled': '▤',
    question: '问',
    consult: '问',
    message: '◇',
    'message-filled': '◆',
    user: '○',
    'user-filled': '●',
  };
  return mapped[icon] || stringValue(item.text, icon).slice(0, 1).toUpperCase();
}

function previewComponentName(type: string) {
  return getWidgetTemplate(type) ?? PreviewUnknown;
}

const PreviewUnknown = RenCommonComponentPreview;

function numberValue(value: unknown, fallback = 0) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function stringValue(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : value == null ? fallback : String(value);
}

function booleanValue(value: unknown, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  return value == null ? fallback : Boolean(value);
}
</script>
